import {
  buildAuthResetPasswordPageMessages,
  ResetPasswordPage,
  useConfirmPasswordResetMutation,
  useSendPasswordResetCodeMutation,
} from "@assetwatch/auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const resetPasswordSearchSchema = z.object({
  code: z.coerce.string().optional(),
  email: z.string().optional(),
  expired: z.union([z.enum(["true", "false"]), z.boolean()]).optional(),
});

type ResetPasswordSearch = z.infer<typeof resetPasswordSearchSchema>;

export const Route = createFileRoute("/login/reset")({
  validateSearch: (search): ResetPasswordSearch =>
    resetPasswordSearchSchema.parse(search),
  component: EnterpriseResetPassword,
});

function isExpiredSearchValue(expired: ResetPasswordSearch["expired"]) {
  return expired === true || expired === "true";
}

export function EnterpriseResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const confirmPasswordResetMutation = useConfirmPasswordResetMutation();
  const sendPasswordResetCodeMutation = useSendPasswordResetCodeMutation();

  return (
    <ResetPasswordPage
      confirmPasswordReset={(variables) =>
        confirmPasswordResetMutation.mutateAsync(variables)
      }
      initialCode={search.code}
      initialEmail={search.email}
      initialExpired={isExpiredSearchValue(search.expired)}
      isConfirming={confirmPasswordResetMutation.isPending}
      isSendingCode={sendPasswordResetCodeMutation.isPending}
      messages={buildAuthResetPasswordPageMessages(t, {
        commonNamespace: "enterprise.common",
        namespace: "enterprise",
      })}
      onNavigateToSignIn={() => void navigate({ to: "/" })}
      sendPasswordResetCode={(username) =>
        sendPasswordResetCodeMutation.mutateAsync({ username })
      }
    />
  );
}
